// Essay Pricing System
// Progressive pricing based on leaderboard ranking and essay content

/**
 * Calculate the credit cost for unlocking a profile's essays
 * @param {number} leaderboardRank - Position on leaderboard (1-based)
 * @param {object} profile - Profile object with essay data
 * @returns {object} Pricing breakdown and total cost
 */
export const calculateEssayPrice = (leaderboardRank, profile) => {
  // Base pricing tiers based on leaderboard ranking
  const getRankingTier = (rank) => {
    if (rank >= 1 && rank <= 5) return 'premium'; // Top 1-5
    if (rank <= 15) return 'high';   // Top 6-15
    if (rank <= 50) return 'medium'; // Top 16-50
    return 'standard'; // 51+
  };

  // Credit costs per tier
  const tierPricing = {
    premium: {
      basePrice: 10,
      label: 'Premium',
      color: '#gold',
      description: 'Top 5 Elite Profiles'
    },
    high: {
      basePrice: 7,
      label: 'High-Value',
      color: '#e74c3c',
      description: 'Top 15 Profiles'
    },
    medium: {
      basePrice: 5,
      label: 'Popular',
      color: '#f39c12',
      description: 'Top 50 Profiles'
    },
    standard: {
      basePrice: 3,
      label: 'Standard',
      color: '#3498db',
      description: 'All Other Profiles'
    }
  };

  const tier = getRankingTier(leaderboardRank);
  const tierInfo = tierPricing[tier];
  const finalPrice = tierInfo.basePrice; // Just use the base price, no discounts or additions
  
  // Count essays for display purposes only
  const essayBreakdown = {
    commonApp: profile.essay ? 1 : 0,
    supplementalSchools: 0,
    supplementalEssays: 0
  };

  // Count supplemental essays for display
  if (profile.supplemental_essays?.schools) {
    essayBreakdown.supplementalSchools = Object.keys(profile.supplemental_essays.schools).length;
    
    Object.values(profile.supplemental_essays.schools).forEach(schoolData => {
      if (schoolData.essays) {
        essayBreakdown.supplementalEssays += Object.keys(schoolData.essays).length;
      }
    });
  } else if (profile.supplemental_essays?.essays) {
    essayBreakdown.supplementalEssays = profile.supplemental_essays.essays.length;
    essayBreakdown.supplementalSchools = 1;
  }

  const totalEssays = essayBreakdown.commonApp + essayBreakdown.supplementalEssays;

  return {
    totalCredits: finalPrice,
    originalPrice: finalPrice,
    bundleDiscount: 0,
    tier: tierInfo,
    ranking: leaderboardRank,
    essayBreakdown,
    totalEssays,
    priceBreakdown: {
      basePrice: tierInfo.basePrice,
      supplementalCost: 0,
      bundleDiscount: 0
    }
  };
};

/**
 * Get pricing display text for leaderboard
 * @param {number} leaderboardRank 
 * @param {object} profile 
 * @returns {string} Formatted pricing text
 */
export const getPricingDisplayText = (leaderboardRank, profile) => {
  const pricing = calculateEssayPrice(leaderboardRank, profile);
  
  if (pricing.totalEssays === 0) {
    return 'No essays available';
  }

  let text = `${pricing.totalCredits} credits`;
  
  if (pricing.bundleDiscount > 0) {
    text += ` (${pricing.originalPrice} - ${pricing.bundleDiscount} discount)`;
  }
  
  // Add essay count info
  const essayCount = pricing.essayBreakdown.commonApp + pricing.essayBreakdown.supplementalEssays;
  text += ` • ${essayCount} essay${essayCount !== 1 ? 's' : ''}`;
  
  return text;
};

/**
 * Get detailed pricing breakdown for display
 * @param {number} leaderboardRank 
 * @param {object} profile 
 * @returns {object} Detailed breakdown for UI
 */
export const getDetailedPricingBreakdown = (leaderboardRank, profile) => {
  const pricing = calculateEssayPrice(leaderboardRank, profile);
  
  const breakdown = [
    {
      item: `${pricing.tier.label} Profile (#${leaderboardRank})`,
      cost: pricing.priceBreakdown.basePrice,
      description: pricing.tier.description
    }
  ];

  if (pricing.priceBreakdown.supplementalCost > 0) {
    breakdown.push({
      item: `${pricing.essayBreakdown.supplementalEssays} Supplemental Essays`,
      cost: pricing.priceBreakdown.supplementalCost,
      description: `${pricing.essayBreakdown.supplementalSchools} school${pricing.essayBreakdown.supplementalSchools !== 1 ? 's' : ''}`
    });
  }

  if (pricing.bundleDiscount > 0) {
    breakdown.push({
      item: 'Bundle Discount',
      cost: -pricing.bundleDiscount,
      description: `${pricing.totalEssays} total essays`
    });
  }

  return {
    breakdown,
    total: pricing.totalCredits,
    tier: pricing.tier,
    essayCount: pricing.totalEssays
  };
};

/**
 * Check if user can afford to unlock essays
 * @param {number} userCredits 
 * @param {number} leaderboardRank 
 * @param {object} profile 
 * @returns {object} Affordability check
 */
export const canAffordEssays = (userCredits, leaderboardRank, profile) => {
  const pricing = calculateEssayPrice(leaderboardRank, profile);
  
  return {
    canAfford: userCredits >= pricing.totalCredits,
    cost: pricing.totalCredits,
    userCredits,
    shortfall: Math.max(0, pricing.totalCredits - userCredits)
  };
};

/**
 * Convert dollars to credits (for reference)
 * Assuming $1 = 2 credits approximately
 */
export const dollarsToCredits = (dollars) => Math.ceil(dollars * 2);
export const creditsToDollars = (credits) => (credits / 2).toFixed(2); 